// Réseau Social Mobile - Page principale avec vraies données
// Menu horizontal: Fil d'actualité, Page Pro, Cinéma, Galerie, Salon, Événement
// Bannière: "Réseau social 100% bâtiment"
// Stories éphémères 7 jours avec texte/emojis

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart, MessageCircle, Share2, Bookmark, MoreHorizontal,
  Play, Pause, Volume2, VolumeX, MapPin, ChevronLeft, ChevronRight,
  Plus, Home, Compass, Video, User, Bell, Search, ArrowLeft,
  Package, Tag, Store, ExternalLink, Map, Layers, Hammer, ShoppingBag,
  Zap, Droplets, TreeDeciduous, Paintbrush, HardHat, Ruler, Flame,
  Building2, ShieldCheck, Wind, Newspaper, Users, Briefcase, Calendar,
  Network, Award, Menu, Wrench, ChevronDown, Camera, SlidersHorizontal,
  X, AlertTriangle, Info, Euro, HelpCircle, AlertCircle, Film, Image,
  MessageSquare, Tv, RefreshCw, Loader2, Clock, ThumbsUp, Eye,
  PlayCircle, BadgeCheck, TrendingUp, Flame as FireIcon, Megaphone
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import UnifiedHeader from '../../components/layout/UnifiedHeader';
import MainNavigation from '../../components/layout/MainNavigation';
import StoriesBar from '../../components/stories/StoriesBar';
import { useWatchTimeTracker, usePostEngagementTracker } from '../../hooks/useWatchTimeTracker';
import { FeedSkeleton, PostSkeleton, CardSkeleton, StoriesBarSkeleton } from '../../components/ui/skeletons';
import { TrustBadgeIcon } from '../../components/badges/TrustBadge';
import { FounderBadgeIcon } from '../../components/badges/FounderBadge';

import { API_URL } from '../../config/api';

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT POST CARD - Style Instagram (données réelles)
// ══════════════════════════════════════════════════════════════════════════════

function PostCard({ post, onLike }) {
  const navigate = useNavigate();
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(post.likes || 0);
  const [saved, setSaved] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleLike = () => {
    setLiked(!liked);
    setLikesCount(prev => liked ? prev - 1 : prev + 1);
    if (onLike) onLike(post.id, !liked);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Récemment';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      
      if (diffHours < 1) return 'À l\'instant';
      if (diffHours < 24) return `Il y a ${diffHours}h`;
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 7) return `Il y a ${diffDays}j`;
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    } catch {
      return 'Récemment';
    }
  };

  // Couleur avatar basée sur le métier
  const getAvatarColor = (metier) => {
    const colors = {
      'Plombier': '#3B82F6',
      'Électricien': '#EAB308',
      'Maçon': '#EA580C',
      'Carreleur': '#10B981',
      'Menuisier': '#D97706',
      'Peintre': '#EC4899',
      'Couvreur': '#8B5CF6',
      'Chauffagiste': '#EF4444',
    };
    return colors[metier] || '#1E3A5F';
  };

  const author = post.author || {};
  const avatarLetter = (author.display_name || author.username || 'A')[0].toUpperCase();
  const displayName = author.display_name || author.username || 'Artisan';
  const isVerified = author.is_verified !== false;
  const metier = author.metier || post.metier_category || 'Artisan';
  const city = author.city || post.location || '';

  // Image principale
  const mainImage = post.photos?.[0] || post.image || null;

  return (
    <article className="bg-white border-b border-gray-100" data-testid={`post-${post.id}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-3">
          {author.profile_photo ? (
            <img 
              src={author.profile_photo} 
              alt={displayName}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
              style={{ backgroundColor: getAvatarColor(metier) }}
            >
              {avatarLetter}
            </div>
          )}
          <div>
            <div className="flex items-center gap-1">
              <span className="font-semibold text-sm text-gray-900">{displayName}</span>
              {isVerified && (
                <BadgeCheck size={16} className="text-blue-500 fill-blue-500" />
              )}
              {author?.is_founder && <FounderBadgeIcon founderNumber={author.founder_number} size={16} />}
              {author?.trust_level && <TrustBadgeIcon trustLevel={author.trust_level} size={16} />}
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>{metier}</span>
              {city && (
                <>
                  <span>•</span>
                  <span className="flex items-center gap-0.5">
                    <MapPin size={10} />
                    {city}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <button className="p-2 hover:bg-gray-100 rounded-full">
          <MoreHorizontal size={20} className="text-gray-500" />
        </button>
      </div>

      {/* Type Badge */}
      {post.type && post.type !== 'post' && (
        <div className="px-3 pb-2">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
            post.type === 'chantier' ? 'bg-orange-100 text-orange-700' :
            post.type === 'avant_apres' ? 'bg-purple-100 text-purple-700' :
            post.type === 'conseil' ? 'bg-green-100 text-green-700' :
            post.type === 'question' ? 'bg-blue-100 text-blue-700' :
            'bg-gray-100 text-gray-700'
          }`}>
            {post.type === 'chantier' && <Hammer size={12} />}
            {post.type === 'avant_apres' && <RefreshCw size={12} />}
            {post.type === 'conseil' && <HelpCircle size={12} />}
            {post.type === 'question' && <MessageCircle size={12} />}
            {post.type === 'chantier' ? 'Chantier' :
             post.type === 'avant_apres' ? 'Avant/Après' :
             post.type === 'conseil' ? 'Conseil' :
             post.type === 'question' ? 'Question' : post.type}
          </span>
        </div>
      )}

      {/* Image - Format 4:5 */}
      {mainImage && !imageError && (
        <div 
          className="relative bg-gray-100"
          style={{ aspectRatio: '4/5' }}
        >
          <img 
            src={mainImage} 
            alt="" 
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
          {/* Multiple photos indicator */}
          {post.photos && post.photos.length > 1 && (
            <div className="absolute top-3 right-3 bg-black/70 text-white text-xs px-2 py-1 rounded-full">
              1/{post.photos.length}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-4">
            <button onClick={handleLike} className="flex items-center gap-1 transition-transform active:scale-90">
              <Heart 
                size={24} 
                className={`transition-colors ${liked ? 'text-red-500 fill-red-500' : 'text-gray-700'}`}
              />
            </button>
            <button className="flex items-center gap-1">
              <MessageCircle size={24} className="text-gray-700" />
            </button>
            <button>
              <Share2 size={22} className="text-gray-700" />
            </button>
          </div>
          <button onClick={() => setSaved(!saved)}>
            <Bookmark 
              size={24} 
              className={saved ? 'text-gray-900 fill-gray-900' : 'text-gray-700'} 
            />
          </button>
        </div>

        {/* Likes count */}
        <p className="font-semibold text-sm text-gray-900 mb-1">
          {likesCount} j'aime
        </p>

        {/* Content */}
        <p className="text-sm text-gray-900">
          <span className="font-semibold">{displayName}</span>{' '}
          {post.content}
        </p>

        {/* Chantier info */}
        {post.chantier_name && (
          <p className="text-sm text-gray-600 mt-1">
            📍 {post.chantier_name} {post.chantier_city && `- ${post.chantier_city}`}
            {post.chantier_progress && ` (${post.chantier_progress}%)`}
          </p>
        )}

        {/* Comments link */}
        {(post.comments || 0) > 0 && (
          <button className="text-sm text-gray-500 mt-1">
            Voir les {post.comments} commentaires
          </button>
        )}

        {/* Date */}
        <p className="text-[10px] text-gray-400 uppercase mt-2">
          {formatDate(post.created_at)}
        </p>
      </div>
    </article>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT REEL CARD - Format vertical vidéo avec tracking watch time
// ══════════════════════════════════════════════════════════════════════════════

function ReelCard({ reel, index, showTrendingBadge = false }) {
  const [playing, setPlaying] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  
  // Hook de tracking watch time
  const watchTracker = useWatchTimeTracker(reel.id, reel.duration || 30);
  
  // Observer pour détecter quand le reel est visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            // Reel visible à plus de 50% - simuler le début de visionnage
            watchTracker.startTracking();
          } else {
            // Reel plus visible - envoyer les données
            watchTracker.pauseTracking();
          }
        });
      },
      { threshold: [0.5] }
    );
    
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    
    return () => {
      observer.disconnect();
      watchTracker.sendViewData();
    };
  }, [reel.id]);
  
  const navigate = useNavigate();
  
  // Click ouvre le mode Cinéma (Reels)
  const handleOpenCinema = () => {
    navigate(`/reseau/reels?start=${reel.id}`);
  };
  
  return (
    <div 
      ref={containerRef}
      className="relative bg-gray-900 rounded-xl overflow-hidden group cursor-pointer" 
      style={{ aspectRatio: '4/5' }}
      data-testid={`reel-${reel.id}`}
      onClick={handleOpenCinema}
    >
      {/* Badge Trending */}
      {showTrendingBadge && (
        <div className="absolute top-2 left-2 z-10 flex items-center gap-1 bg-gradient-to-r from-orange-500 to-red-500 text-white text-[10px] font-bold px-2 py-1 rounded-full">
          <TrendingUp size={10} />
          <span>#{index + 1}</span>
        </div>
      )}
      
      {/* Badge Vidéo */}
      <div className="absolute top-2 right-2 z-10 flex items-center gap-1 bg-black/60 backdrop-blur text-white text-xs font-medium px-2 py-1 rounded-full">
        <Play size={12} fill="white" />
        <span>{typeof reel.duration === 'number' 
          ? `${Math.floor(reel.duration / 60)}:${(reel.duration % 60).toString().padStart(2, '0')}` 
          : reel.duration || '0:30'}</span>
      </div>
      
      {reel.thumbnail || reel.photos?.[0] ? (
        <img 
          src={reel.thumbnail || reel.photos[0]} 
          alt="" 
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1E3A5F] to-[#F97316]">
          <PlayCircle size={48} className="text-white/80" />
        </div>
      )}
      
      {/* Play button overlay centré */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-14 h-14 rounded-full bg-black/40 backdrop-blur flex items-center justify-center group-hover:scale-110 transition-transform">
          <Play size={28} className="text-white ml-1" fill="white" />
        </div>
      </div>
      
      {/* Info overlay */}
      <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
        {/* Author */}
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-bold">
            {(reel.author?.display_name || reel.author?.username || 'A')[0].toUpperCase()}
          </div>
          <span className="text-white text-sm font-medium">{reel.author?.display_name || reel.author?.username || 'Artisan'}</span>
          {reel.author?.is_verified && <BadgeCheck size={14} className="text-blue-400 fill-blue-400" />}
        </div>
        <p className="text-white text-sm line-clamp-2">{reel.content || reel.title}</p>
        <div className="flex items-center gap-4 mt-2 text-white/80 text-xs">
          <span className="flex items-center gap-1"><Eye size={12} /> {(reel.views || 0).toLocaleString()}</span>
          <span className="flex items-center gap-1"><Heart size={12} /> {(reel.likes || 0).toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PAGE PRO CARD
// ══════════════════════════════════════════════════════════════════════════════

function PageProCard({ page }) {
  const navigate = useNavigate();
  
  return (
    <div 
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => navigate(`/boutique/${page.slug || page.id}`)}
      data-testid={`page-pro-${page.id}`}
    >
      {/* Cover */}
      <div className="h-24 bg-gradient-to-r from-[#1E3A5F] to-[#2D4A6F] relative">
        {page.cover_image && (
          <img src={page.cover_image} alt="" className="w-full h-full object-cover" />
        )}
      </div>
      
      {/* Avatar */}
      <div className="px-4 -mt-8 relative">
        <div className="w-16 h-16 rounded-full bg-white border-4 border-white shadow-md overflow-hidden">
          {page.logo || page.profile_photo ? (
            <img src={page.logo || page.profile_photo} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-[#F97316] flex items-center justify-center text-white font-bold text-xl">
              {(page.business_name || page.name || 'P')[0]}
            </div>
          )}
        </div>
      </div>
      
      {/* Info */}
      <div className="p-4 pt-2">
        <div className="flex items-center gap-1">
          <h3 className="font-bold text-gray-900">{page.business_name || page.name}</h3>
          {page.is_verified && <BadgeCheck size={16} className="text-blue-500 fill-blue-500" />}
        </div>
        <p className="text-sm text-gray-500">{page.metier || page.category}</p>
        {page.city && (
          <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
            <MapPin size={12} /> {page.city}
          </p>
        )}
        <div className="flex items-center gap-3 mt-3 text-xs text-gray-500">
          <span>{page.followers_count || 0} abonnés</span>
          <span>{page.posts_count || 0} posts</span>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT SALON CARD
// ══════════════════════════════════════════════════════════════════════════════

function SalonCard({ salon }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow" data-testid={`salon-${salon.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1E3A5F] to-[#F97316] flex items-center justify-center flex-shrink-0">
          <MessageSquare size={24} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-gray-900">{salon.name || salon.title}</h3>
          <p className="text-sm text-gray-500 line-clamp-2">{salon.description}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
            <span className="flex items-center gap-1"><Users size={12} /> {salon.members_count || 0} membres</span>
            <span className="flex items-center gap-1"><MessageCircle size={12} /> {salon.messages_count || 0} messages</span>
          </div>
        </div>
      </div>
      <button className="w-full mt-3 py-2 bg-[#1E3A5F] text-white rounded-lg text-sm font-medium hover:bg-[#2D4A6F] transition-colors">
        Rejoindre
      </button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT ÉVÉNEMENT CARD
// ══════════════════════════════════════════════════════════════════════════════

function EvenementCard({ event }) {
  const formatEventDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow" data-testid={`event-${event.id}`}>
      {/* Image */}
      <div className="h-32 bg-gradient-to-br from-[#1E3A5F] to-[#F97316] relative">
        {event.image && (
          <img src={event.image} alt="" className="w-full h-full object-cover" />
        )}
        <div className="absolute top-3 left-3 bg-white rounded-lg px-2 py-1 text-center shadow">
          <p className="text-xs font-bold text-[#F97316]">{formatEventDate(event.date || event.event_date)}</p>
        </div>
      </div>
      
      {/* Info */}
      <div className="p-4">
        <h3 className="font-bold text-gray-900 line-clamp-2">{event.title || event.name}</h3>
        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{event.description}</p>
        {event.location && (
          <p className="text-xs text-gray-400 flex items-center gap-1 mt-2">
            <MapPin size={12} /> {event.location}
          </p>
        )}
        <div className="flex items-center gap-2 mt-3">
          <span className="text-xs text-gray-500">{event.attendees_count || 0} participants</span>
        </div>
        <button className="w-full mt-3 py-2 bg-[#F97316] text-white rounded-lg text-sm font-medium hover:bg-[#EA580C] transition-colors">
          S'inscrire
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT GALERIE ITEM
// ══════════════════════════════════════════════════════════════════════════════

function GalerieItem({ item }) {
  return (
    <div className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden group cursor-pointer" data-testid={`galerie-${item.id}`}>
      <img 
        src={item.image || item.photos?.[0]} 
        alt="" 
        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
      />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
        <div className="opacity-0 group-hover:opacity-100 flex items-center gap-4 text-white">
          <span className="flex items-center gap-1"><Heart size={18} /> {item.likes || 0}</span>
          <span className="flex items-center gap-1"><MessageCircle size={18} /> {item.comments || 0}</span>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT ÉTAT VIDE
// ══════════════════════════════════════════════════════════════════════════════

function EmptyState({ icon: Icon, title, description }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center" data-testid="empty-state">
      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
        <Icon size={28} className="text-gray-400" />
      </div>
      <h3 className="text-lg font-semibold text-gray-700 mb-2">{title}</h3>
      <p className="text-sm text-gray-500 max-w-xs">{description}</p>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PAGE PRINCIPALE RÉSEAU MOBILE
// ══════════════════════════════════════════════════════════════════════════════

export default function ReseauMobilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('fil');
  const [activeFilter, setActiveFilter] = useState('tout'); // Filtre: tout, a_cote, suivi, offres
  const [selectedMetier, setSelectedMetier] = useState(null); // Métier sélectionné
  const [userLocation, setUserLocation] = useState(null); // Géolocalisation utilisateur
  
  // Liste des métiers - Style Premium SANS EMOJIS
  const METIERS = [
    { id: 'jardinier', label: 'Jardinier', color: '#22C55E' },
    { id: 'pisciniste', label: 'Pisciniste', color: '#06B6D4' },
    { id: 'electricien', label: 'Électricien', color: '#EAB308' },
    { id: 'plombier', label: 'Plombier', color: '#3B82F6' },
    { id: 'macon', label: 'Maçon', color: '#EA580C' },
    { id: 'carreleur', label: 'Carreleur', color: '#10B981' },
    { id: 'peintre', label: 'Peintre', color: '#EC4899' },
    { id: 'menuisier', label: 'Menuisier', color: '#D97706' },
    { id: 'couvreur', label: 'Couvreur', color: '#8B5CF6' },
    { id: 'chauffagiste', label: 'Chauffagiste', color: '#EF4444' },
    { id: 'paysagiste', label: 'Paysagiste', color: '#16A34A' },
    { id: 'climaticien', label: 'Climaticien', color: '#0891B2' },
    { id: 'facadier', label: 'Façadier', color: '#A855F7' },
    { id: 'serrurier', label: 'Serrurier', color: '#64748B' },
    { id: 'vitrier', label: 'Vitrier', color: '#06B6D4' },
    { id: 'charpentier', label: 'Charpentier', color: '#92400E' },
    { id: 'plaquiste', label: 'Plaquiste', color: '#6366F1' },
    { id: 'terrassier', label: 'Terrassier', color: '#CA8A04' },
    { id: 'cuisiniste', label: 'Cuisiniste', color: '#F97316' },
    { id: 'architecte', label: 'Architecte', color: '#1E3A5F' },
    { id: 'geometre', label: 'Géomètre', color: '#475569' },
    { id: 'expert_batiment', label: 'Expert', color: '#0F172A' },
    { id: 'bureau_etudes', label: 'Bureau études', color: '#334155' },
    { id: 'diagnostiqueur', label: 'Diagnostic', color: '#059669' },
    { id: 'maitre_oeuvre', label: 'Maître œuvre', color: '#B45309' },
  ];
  
  // États des données
  const [posts, setPosts] = useState([]);
  const [pagesPro, setPagesPro] = useState([]);
  const [reels, setReels] = useState([]);
  const [salons, setSalons] = useState([]);
  const [evenements, setEvenements] = useState([]);
  const [galerie, setGalerie] = useState([]);
  
  // États Trending
  const [trendingPosts, setTrendingPosts] = useState([]);
  const [trendingReels, setTrendingReels] = useState([]);
  const [trendingPeriod, setTrendingPeriod] = useState('7d'); // 24h, 7d, 30d
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Déterminer l'onglet actif depuis l'URL
  useEffect(() => {
    const path = location.pathname;
    if (path === '/reseau' || path === '/reseau/') {
      setActiveTab('fil');
    } else if (path.includes('/reseau/pages')) {
      setActiveTab('pages');
    } else if (path.includes('/reseau/cinema')) {
      setActiveTab('cinema');
    } else if (path.includes('/reseau/galerie')) {
      setActiveTab('galerie');
    } else if (path.includes('/reseau/salon')) {
      setActiveTab('salon');
    } else if (path.includes('/reseau/evenements')) {
      setActiveTab('evenements');
    } else if (path.includes('/reseau/trending')) {
      setActiveTab('trending');
    }
  }, [location.pathname]);

  // Charger les données
  useEffect(() => {
    loadData();
  }, [activeTab, selectedMetier]);

  // Charger les données de sidebar au montage initial
  useEffect(() => {
    const loadSidebarData = async () => {
      // Charger en parallèle pour le sidebar
      await Promise.all([
        loadPagesPro(),
        loadEvenements()
      ]);
    };
    loadSidebarData();
  }, []);

  // Recharger les tendances quand la période change
  useEffect(() => {
    if (activeTab === 'trending') {
      loadTrending();
    }
  }, [trendingPeriod]);


  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      switch (activeTab) {
        case 'fil':
          await loadPosts();
          break;
        case 'pages':
          await loadPagesPro();
          break;
        case 'cinema':
          await loadReels();
          break;
        case 'galerie':
          await loadGalerie();
          break;
        case 'salon':
          await loadSalons();
          break;
        case 'evenements':
          await loadEvenements();
          break;
        case 'trending':
          await loadTrending();
          break;
      }
    } catch (e) {
      console.error('Error loading data:', e);
      setError('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadPosts = async () => {
    try {
      // Construire l'URL avec les filtres
      let url = `${API_URL}/api/reseau/posts?limit=30`;
      if (activeFilter === 'metier' && selectedMetier) {
        url += `&filter_type=metier&metier=${selectedMetier}`;
      }
      
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setPosts(data.posts || []);
      } else {
        console.error('Erreur chargement posts:', res.status);
        setPosts([]);
      }
    } catch (e) {
      console.error('Erreur chargement posts:', e);
      setPosts([]);
    }
  };

  const loadPagesPro = async () => {
    try {
      const res = await fetch(`${API_URL}/api/reseau/pages-pro?limit=20`);
      if (res.ok) {
        const data = await res.json();
        setPagesPro(data.pages || []);
      } else {
        setPagesPro([]);
      }
    } catch {
      setPagesPro([]);
    }
  };

  const loadReels = async () => {
    try {
      let url = `${API_URL}/api/reseau/reels?limit=20`;
      if (activeFilter === 'metier' && selectedMetier) {
        url += `&metier=${selectedMetier}`;
      }
      
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setReels(data.reels || []);
      } else {
        setReels([]);
      }
    } catch {
      setReels([]);
    }
  };

  const loadGalerie = async () => {
    try {
      let url = `${API_URL}/api/reseau/galerie?limit=30`;
      if (activeFilter === 'metier' && selectedMetier) {
        url += `&metier=${selectedMetier}`;
      }
      
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setGalerie(data.galerie || []);
      } else {
        setGalerie([]);
      }
    } catch {
      setGalerie([]);
    }
  };

  const loadSalons = async () => {
    try {
      const res = await fetch(`${API_URL}/api/reseau/salons?limit=20`);
      if (res.ok) {
        const data = await res.json();
        setSalons(data.salons || []);
      } else {
        setSalons([]);
      }
    } catch {
      setSalons([]);
    }
  };

  const loadEvenements = async () => {
    try {
      const res = await fetch(`${API_URL}/api/reseau/evenements?limit=20&upcoming_only=false`);
      if (res.ok) {
        const data = await res.json();
        setEvenements(data.evenements || []);
      } else {
        setEvenements([]);
      }
    } catch {
      setEvenements([]);
    }
  };

  // ══════════════════════════════════════════════════════════════════════════════
  // CHARGEMENT TRENDING - Algorithme de recommandation
  // ══════════════════════════════════════════════════════════════════════════════
  
  const loadTrending = async () => {
    try {
      // Charger les posts trending et les reels trending en parallèle
      const [postsRes, reelsRes] = await Promise.all([
        fetch(`${API_URL}/api/reseau/trending/posts?period=${trendingPeriod}&limit=20`),
        fetch(`${API_URL}/api/reseau/trending/reels?period=${trendingPeriod}&limit=10`)
      ]);
      
      if (postsRes.ok) {
        const data = await postsRes.json();
        setTrendingPosts(data.posts || []);
      } else {
        setTrendingPosts([]);
      }
      
      if (reelsRes.ok) {
        const data = await reelsRes.json();
        setTrendingReels(data.reels || []);
      } else {
        setTrendingReels([]);
      }
    } catch (e) {
      console.error('Error loading trending:', e);
      setTrendingPosts([]);
      setTrendingReels([]);
    }
  };

  const handleLike = async (postId, liked) => {
    // TODO: Implémenter like API
    console.log('Like:', postId, liked);
  };

  // Rendu du contenu selon l'onglet actif
  const renderContent = () => {
    if (loading) {
      // Skeletons dynamiques selon l'onglet
      switch (activeTab) {
        case 'fil':
          return <FeedSkeleton />;
        case 'pages':
        case 'salon':
        case 'evenement':
          return (
            <div className="grid grid-cols-1 gap-4 p-4">
              {[1, 2, 3, 4].map((i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <CardSkeleton />
                </motion.div>
              ))}
            </div>
          );
        case 'cinema':
          return (
            <div className="grid grid-cols-2 gap-2 p-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.08 }}
                  className="aspect-[9/16] rounded-xl overflow-hidden"
                >
                  <div className="w-full h-full bg-gray-200 animate-pulse relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer" />
                  </div>
                </motion.div>
              ))}
            </div>
          );
        case 'galerie':
          return (
            <div className="grid grid-cols-3 gap-1 p-1">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.05 }}
                  className="aspect-square bg-gray-200 animate-pulse"
                />
              ))}
            </div>
          );
        default:
          return (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={32} className="animate-spin text-[#1E3A5F]" />
            </div>
          );
      }
    }

    switch (activeTab) {
      case 'fil':
        return (
          <div className="pb-20">
            {/* ═══ STORIES - 7 jours avec texte/emojis ═══ */}
            <StoriesBar onCreateStory={() => navigate('/cockpit-pro/reseau-social/story/create')} />

            {/* ═══ FEED PRINCIPAL - DONNÉES RÉELLES ═══ */}
            <div className="bg-white" data-testid="feed-container">
              {posts.length > 0 ? (
                posts.map((post) => (
                  <PostCard key={post.id} post={post} onLike={handleLike} />
                ))
              ) : (
                <EmptyState 
                  icon={Newspaper} 
                  title="Aucune publication" 
                  description="Sois le premier à partager tes réalisations !" 
                />
              )}
            </div>
          </div>
        );

      case 'pages':
        return pagesPro.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 p-4">
            {pagesPro.map((page) => (
              <PageProCard key={page.id} page={page} />
            ))}
          </div>
        ) : (
          <EmptyState icon={Store} title="Aucune page pro" description="Les artisans n'ont pas encore créé leur page" />
        );

      case 'cinema':
        return reels.length > 0 ? (
          <div className="grid grid-cols-2 gap-2 p-4">
            {reels.map((reel, i) => (
              <ReelCard key={reel.id} reel={reel} index={i} />
            ))}
          </div>
        ) : (
          <EmptyState icon={Film} title="Aucune vidéo" description="Les artisans n'ont pas encore partagé de vidéos" />
        );

      case 'galerie':
        return galerie.length > 0 ? (
          <div className="grid grid-cols-3 gap-1 p-1">
            {galerie.map((item) => (
              <GalerieItem key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <EmptyState icon={Image} title="Aucune photo" description="La galerie est vide pour le moment" />
        );

      case 'salon':
        return salons.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 p-4">
            {salons.map((salon) => (
              <SalonCard key={salon.id} salon={salon} />
            ))}
          </div>
        ) : (
          <EmptyState icon={MessageSquare} title="Aucun salon" description="Les salons de discussion arrivent bientôt" />
        );

      case 'evenements':
        return evenements.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 p-4">
            {evenements.map((event) => (
              <EvenementCard key={event.id} event={event} />
            ))}
          </div>
        ) : (
          <EmptyState icon={Calendar} title="Aucun événement" description="Pas d'événement prévu pour le moment" />
        );

      case 'trending':
        return (
          <div className="p-4 space-y-6">
            {/* Header Trending */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <TrendingUp size={24} className="text-orange-500" />
                <h2 className="text-lg font-bold text-gray-900">Tendances</h2>
              </div>
              
              {/* Sélecteur de période */}
              <div className="flex gap-1 bg-gray-100 rounded-full p-1">
                {[
                  { id: '24h', label: '24h' },
                  { id: '7d', label: '7 jours' },
                  { id: '30d', label: '30 jours' },
                ].map((period) => (
                  <button
                    key={period.id}
                    onClick={() => {
                      setTrendingPeriod(period.id);
                      // loadTrending will be called by useEffect when trendingPeriod changes
                    }}
                    className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${
                      trendingPeriod === period.id
                        ? 'bg-[#1E3A5F] text-white'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {period.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Section Reels Trending */}
            {trendingReels.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Film size={18} className="text-[#1E3A5F]" />
                  <h3 className="text-sm font-semibold text-gray-800">🔥 Vidéos populaires</h3>
                  <span className="text-xs text-gray-500">({trendingReels.length})</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {trendingReels.slice(0, 4).map((reel, i) => (
                    <ReelCard key={reel.id} reel={reel} index={i} showTrendingBadge={true} />
                  ))}
                </div>
              </div>
            )}
            
            {/* Section Posts Trending */}
            {trendingPosts.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Newspaper size={18} className="text-[#1E3A5F]" />
                  <h3 className="text-sm font-semibold text-gray-800">🔥 Posts populaires</h3>
                  <span className="text-xs text-gray-500">({trendingPosts.length})</span>
                </div>
                <div className="space-y-4">
                  {trendingPosts.map((post, i) => (
                    <div key={post.id} className="relative">
                      {/* Badge de rang */}
                      <div className="absolute -left-2 top-4 z-10 w-6 h-6 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center text-white text-xs font-bold shadow-lg">
                        {i + 1}
                      </div>
                      <PostCard post={post} onLike={handleLike} />
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Empty state */}
            {trendingPosts.length === 0 && trendingReels.length === 0 && (
              <EmptyState 
                icon={TrendingUp} 
                title="Pas encore de tendances" 
                description="Les contenus populaires apparaîtront ici au fil du temps" 
              />
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Vérifier si on est dans le ReseauLayout (via useOutletContext)
  const outletContext = useOutletContext();
  const isInReseauLayout = !!outletContext;
  
  // Si on est dans ReseauLayout, utiliser les filtres du layout
  useEffect(() => {
    if (outletContext?.activeFilter) {
      setActiveFilter(outletContext.activeFilter);
    }
  }, [outletContext?.activeFilter]);

  // ══════════════════════════════════════════════════════════════════════════════
  // COMPOSANTS SIDEBAR DESKTOP
  // ══════════════════════════════════════════════════════════════════════════════
  
  const LeftSidebar = () => (
    <div className="space-y-4">
      {/* Profil utilisateur */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#1E3A5F] to-[#2D4A6F] flex items-center justify-center text-white font-bold">
            U
          </div>
          <div>
            <p className="font-semibold text-gray-900">Mon Profil</p>
            <p className="text-sm text-gray-500">Particulier</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center border-t border-gray-100 pt-3">
          <div>
            <p className="font-bold text-gray-900">12</p>
            <p className="text-xs text-gray-500">Posts</p>
          </div>
          <div>
            <p className="font-bold text-gray-900">48</p>
            <p className="text-xs text-gray-500">Abonnés</p>
          </div>
          <div>
            <p className="font-bold text-gray-900">156</p>
            <p className="text-xs text-gray-500">Abonnements</p>
          </div>
        </div>
      </div>
      
      {/* Navigation rapide */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="font-semibold text-gray-900 mb-3">Navigation</h3>
        <nav className="space-y-1">
          {[
            { icon: Home, label: 'Fil d\'actualité', path: '/reseau', active: activeTab === 'fil' },
            { icon: Store, label: 'Pages Pro', path: '/reseau/pages', active: activeTab === 'pages' },
            { icon: Film, label: 'Cinéma', path: '/reseau/cinema', active: activeTab === 'cinema' },
            { icon: Image, label: 'Galerie', path: '/reseau/galerie', active: activeTab === 'galerie' },
            { icon: MessageSquare, label: 'Salons', path: '/reseau/salon', active: activeTab === 'salon' },
            { icon: Calendar, label: 'Événements', path: '/reseau/evenements', active: activeTab === 'evenements' },
            { icon: TrendingUp, label: 'Tendances', path: '/reseau/trending', active: activeTab === 'trending' },
          ].map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                item.active 
                  ? 'bg-[#1E3A5F] text-white' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <item.icon size={18} />
              <span className="text-sm font-medium">{item.label}</span>
            </button>
          ))}
        </nav>
      </div>
      
      {/* Filtres par métier */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="font-semibold text-gray-900 mb-3">Filtrer par métier</h3>
        <div className="space-y-1 max-h-[300px] overflow-y-auto">
          <button
            onClick={() => setSelectedMetier(null)}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
              !selectedMetier 
                ? 'bg-[#1E3A5F] text-white' 
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <span>Tous les métiers</span>
          </button>
          {METIERS.slice(0, 10).map((metier) => (
            <button
              key={metier.id}
              onClick={() => setSelectedMetier(metier.id)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                selectedMetier === metier.id 
                  ? 'text-white' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              style={selectedMetier === metier.id ? { backgroundColor: metier.color } : {}}
            >
              <div 
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: metier.color }}
              />
              <span>{metier.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
  
  const RightSidebar = () => (
    <div className="space-y-4">
      {/* Créer un post */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <button 
          className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-[#E8650A] to-[#F97316] text-white rounded-xl font-medium hover:shadow-lg transition-all"
          onClick={() => navigate('/cockpit-pro/reseau-social/posts')}
        >
          <Plus size={20} />
          <span>Créer un post</span>
        </button>
      </div>
      
      {/* Suggestions de pros */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="font-semibold text-gray-900 mb-3">Suggestions</h3>
        <div className="space-y-3">
          {pagesPro.slice(0, 4).map((page) => (
            <div key={page.id} className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                style={{ backgroundColor: '#1E3A5F' }}
              >
                {(page.business_name || page.name || 'A')[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-gray-900 truncate">{page.business_name || page.name}</p>
                <p className="text-xs text-gray-500">{page.metier}</p>
              </div>
              <button className="px-3 py-1 text-xs font-medium text-[#E8650A] border border-[#E8650A] rounded-full hover:bg-[#E8650A] hover:text-white transition-all">
                Suivre
              </button>
            </div>
          ))}
          {pagesPro.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-2">Aucune suggestion pour le moment</p>
          )}
        </div>
      </div>
      
      {/* Événements à venir */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="font-semibold text-gray-900 mb-3">Événements à venir</h3>
        <div className="space-y-3">
          {evenements.slice(0, 2).map((event) => (
            <div key={event.id} className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#1E3A5F] to-[#F97316] flex items-center justify-center flex-shrink-0">
                <Calendar size={18} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-gray-900 line-clamp-1">{event.title || event.name}</p>
                <p className="text-xs text-gray-500">{event.date || event.event_date}</p>
              </div>
            </div>
          ))}
          {evenements.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-2">Aucun événement prévu</p>
          )}
        </div>
      </div>
      
      {/* Tendances */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <TrendingUp size={16} className="text-[#E8650A]" />
          Tendances
        </h3>
        <div className="space-y-2">
          {['#RénovationÉnergétique', '#Plomberie', '#Électricité', '#Carrelage', '#PompeÀChaleur'].map((tag, i) => (
            <div key={tag} className="flex items-center justify-between">
              <span className="text-sm text-[#1E3A5F] font-medium">{tag}</span>
              <span className="text-xs text-gray-400">{Math.floor(Math.random() * 500) + 100} posts</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div 
      className="bg-[#F8FAFC] font-['Inter',sans-serif] min-h-screen" 
      data-testid="reseau-mobile-page"
    >
      {/* Header & Navigation - Seulement si PAS dans ReseauLayout */}
      {!isInReseauLayout && (
        <>
          <UnifiedHeader />
          <MainNavigation />
          <div className="h-11 md:hidden" />
          <div className="hidden md:block" style={{ height: '100px' }} />
        </>
      )}

      {/* ═══ BANNIÈRE RÉSEAU SOCIAL - Seulement si PAS dans ReseauLayout ═══ */}
      {!isInReseauLayout && (
        <div className="bg-gradient-to-r from-[#1E3A5F] via-[#2D4A6F] to-[#1E3A5F] relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)'
            }} />
          </div>
          
          {/* Version MOBILE - Bannière Premium Apple/Stripe */}
          <div className="md:hidden px-4 pt-6 pb-3">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-white whitespace-nowrap leading-none">
                Réseau social 100% bâtiment
              </h1>
              <p className="text-white/90 text-sm font-normal whitespace-nowrap leading-none mt-2">
                <span className="font-semibold">1 personne</span> = <span className="font-semibold">1 compte</span> = <span className="font-semibold">0 faux profil</span>
              </p>
            </div>
          </div>
          
          {/* Version DESKTOP/TABLET - Plus compact */}
          <div className="hidden md:block px-4 py-4 relative text-center">
            <div className="flex items-center justify-center gap-2">
              <Users size={22} className="text-white" />
              <h1 className="text-xl font-bold text-white">
                Réseau social 100% bâtiment
              </h1>
              <span className="text-white/80 text-sm ml-2">
                • 1 personne = 1 compte = 0 faux profil
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ═══ FILTRES RÉSEAU MOBILE - Seulement si PAS dans ReseauLayout ═══ */}
      {!isInReseauLayout && (
      <div className="lg:hidden bg-white">
        {/* Filtres principaux: Tout | À côté | Suivi | Offres */}
        <div className="py-2 flex justify-center">
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-[#f0f0f0]">
            {[
              { id: 'tout', label: 'Tout' },
              { id: 'a_cote', label: 'À côté' },
              { id: 'suivi', label: 'Suivi' },
              { id: 'offres', label: 'Offres' },
            ].map((filter) => (
              <button
                key={filter.id}
                onClick={() => {
                  setActiveFilter(filter.id);
                  if (filter.id === 'a_cote' && !userLocation) {
                    // Demander la géolocalisation
                    if (navigator.geolocation) {
                      navigator.geolocation.getCurrentPosition(
                        (position) => {
                          setUserLocation({
                            lat: position.coords.latitude,
                            lng: position.coords.longitude
                          });
                          toast.success('Position détectée');
                        },
                        () => {
                          toast.error('Impossible de te localiser');
                        }
                      );
                    }
                  }
                }}
                className="flex items-center gap-1 rounded-full whitespace-nowrap transition-all duration-200"
                style={{
                  fontSize: '12px',
                  padding: '6px 14px',
                  fontWeight: 600,
                  backgroundColor: activeFilter === filter.id ? '#1E3A5F' : 'transparent',
                  color: activeFilter === filter.id ? '#ffffff' : '#555555',
                }}
                data-testid={`reseau-filter-${filter.id}`}
              >
                <span>{filter.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Sous-filtres Métiers - Scroll horizontal premium SANS EMOJIS */}
        <div 
          className="overflow-x-auto scrollbar-hide border-t border-gray-100"
          style={{ 
            background: 'linear-gradient(180deg, #FAFAFA 0%, #FFFFFF 100%)',
          }}
        >
          <div className="flex gap-2 px-4 py-3" style={{ width: 'max-content' }}>
            {/* Bouton "Tous" */}
            <button
              onClick={() => setSelectedMetier(null)}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-all whitespace-nowrap ${
                !selectedMetier
                  ? 'bg-[#1E3A5F] text-white shadow-sm'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-400'
              }`}
            >
              Tous
            </button>
            
            {/* Liste des métiers - Style Premium */}
            {METIERS.map((metier) => (
              <button
                key={metier.id}
                onClick={() => setSelectedMetier(metier.id)}
                className={`px-4 py-2 rounded-full text-xs font-semibold transition-all whitespace-nowrap ${
                  selectedMetier === metier.id
                    ? 'text-white shadow-sm'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-400'
                }`}
                style={selectedMetier === metier.id ? { backgroundColor: metier.color } : {}}
                data-testid={`metier-${metier.id}`}
              >
                {metier.label}
              </button>
            ))}
          </div>
        </div>

        {/* Indicateur du métier sélectionné */}
        {selectedMetier && (
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div 
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: METIERS.find(m => m.id === selectedMetier)?.color }}
                />
                <span className="text-xs font-semibold text-gray-700">
                  {METIERS.find(m => m.id === selectedMetier)?.label}
                </span>
              </div>
              <button 
                onClick={() => setSelectedMetier(null)}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Effacer
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════════
          LAYOUT DESKTOP - 3 COLONNES (Sidebar - Feed - Sidebar)
          ══════════════════════════════════════════════════════════════════════════ */}
      <div className="hidden lg:block">
        <div className="max-w-[1400px] mx-auto px-6 py-6">
          <div className="grid grid-cols-12 gap-6">
            {/* Colonne Gauche - Navigation & Filtres */}
            <div className="col-span-3">
              <div className="sticky top-[120px]">
                <LeftSidebar />
              </div>
            </div>
            
            {/* Colonne Centrale - Feed */}
            <div className="col-span-6">
              <div className="max-w-[500px] mx-auto">
                {renderContent()}
              </div>
            </div>
            
            {/* Colonne Droite - Suggestions & Tendances */}
            <div className="col-span-3">
              <div className="sticky top-[120px]">
                <RightSidebar />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════════
          LAYOUT MOBILE/TABLET - Pleine largeur
          ══════════════════════════════════════════════════════════════════════════ */}
      <div className="lg:hidden">
        <div className={isInReseauLayout ? "" : "pb-20"}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
